import { FunctionComponent, ReactElement } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "rivals/components/shared/Button";
import Spinner from "rivals/components/shared/Spinner";
import { removeAddOn } from "rivals/redux/subscriptionAddons/actions";
import { getSubmitting as getSubscriptionSubmitting } from "rivals/redux/user/selectors";
import { HOST_BASE_PATH, PROTOCOL } from "rivals/services/base";
import { CUSTOM_EVENTS } from "rivals/shared/analytics/customEvents";
import {
  FREE_ACCOUNT,
  FREE_ACCOUNT_STATUS,
  PATHS,
  PLANS
} from "rivals/shared/constants";
import { AddOn } from "rivals/shared/interfaces/AddOn";
import { setData } from "rivals/shared/utils/qa";
import { AssociatedAddOnCard } from "./AssociatedAddOnCard";
import styles from "./SubscriptionCard.module.scss";
import SubscriptionCardUpsells from "./SubscriptionCardUpsells";
import strings from "./strings";
import { OwnProps } from "./types";

const {
  editSubscriptionText,
  navigateToAllSubscriptions,
  nextBillingDate: nextBillingDateString,
  renewingTitle,
  subTotalText,
  taxText,
  totalText,
  upgradeDefaultText
} = strings;

const SubscriptionCard: FunctionComponent<OwnProps> = ({
  customActionButton,
  buyAddOnModalTrigger,
  cancelSubscriptionText,
  editingSubscription = null,
  sendGAEvent,
  setEditingSubscription,
  showReactivateModal,
  subscription,
  upgradeText,
  upsellAddOns = [],
  userInitiatedCancellationsEnabled,
  showRemoveAddOnModal
}): ReactElement => {
  if (!subscription) {
    return (
      <div className={styles.subscriptionCard}>
        <div className={styles.unsubscribedDescription}>{FREE_ACCOUNT}</div>
        <div className={styles.unsubscribedContent}>{FREE_ACCOUNT_STATUS}</div>
      </div>
    );
  }

  const {
    accountSubscriptionsUrl,
    associatedAddOns,
    cancellable,
    description,
    id: subscriptionId,
    isCancelling,
    isGift,
    isUpgradeable,
    isWeb,
    nextBillingData,
    nextBillingDate,
    plan,
    promoInfo,
    serviceDisplayName,
    realPlanAmount,
    site,
    statusDetails
  } = subscription;

  const dispatch = useDispatch();
  const isSubscriptionSubmitting = useSelector(getSubscriptionSubmitting);
  const canCancel = userInitiatedCancellationsEnabled && cancellable;
  const canEdit = [PLANS.MONTHLY, PLANS.ANNUAL].includes(plan);
  const canReactivate = isCancelling && plan !== PLANS.COMPED;
  const dataPhilter = "subscription-card";
  const editMode = editingSubscription == subscriptionId;

  const removeAddOnAction = (addOn: AddOn): (() => void) => {
    return (): void => {
      dispatch(removeAddOn(subscriptionId, addOn.id));
    };
  };

  const toggleEditSubscription = (): void | undefined =>
    setEditingSubscription &&
    setEditingSubscription(editingSubscription == null ? subscriptionId : null);

  const onClickSubscriptionCancellationLink = (): void => {
    if (sendGAEvent) {
      const clickText = cancelSubscriptionText || "";
      sendGAEvent(
        CUSTOM_EVENTS.clickNavigation(
          PATHS.SUBSCRIPTION_CANCELLATION,
          clickText
        )
      );
    }
  };

  const triShadeHeader = (
    <div className={styles.triShaderHeaderContainer}>
      <div
        className={styles.triShaderHeaderLong}
        style={{
          backgroundColor: `#${site.primaryColor}`
        }}
      />
      <div className={styles.triShaderHeaderMedium} />
      <div className={styles.triShaderHeaderShort} />
    </div>
  );

  const editModeHeader = editMode && setEditingSubscription && (
    <div className={styles.editingSubscriptionHeader}>
      <div className={styles.editingTitle}>Edit Subscription</div>
      <Button
        buttonText={navigateToAllSubscriptions}
        className={styles.backToSubscriptions}
        inverted
        onClick={(): void => toggleEditSubscription()}
      />
    </div>
  );

  const billingDate = nextBillingDate && (
    <span {...setData(dataPhilter, "next-billing-date")}>
      {renewingTitle(nextBillingDate)}
    </span>
  );

  const webEditButtons = (
    <>
      {isUpgradeable && (
        <Button
          buttonText={upgradeText || upgradeDefaultText}
          className={styles.upgradeButton}
          url={`${PROTOCOL}${site.shortName.toLowerCase()}.${HOST_BASE_PATH}/users/subscriptions/${subscriptionId}/upgrade`}
          {...setData(dataPhilter, "upgrade")}
        />
      )}
      {canCancel && (
        <Button
          buttonText={`${cancelSubscriptionText}`}
          data={`${dataPhilter}-cancel`}
          inverted
          onClick={onClickSubscriptionCancellationLink}
          url={PATHS.SUBSCRIPTION_CANCELLATION.replace(
            "[id]",
            `${subscriptionId}`
          )}
        />
      )}
    </>
  );

  const reactivateButton = (
    <Button
      buttonText="Reactivate"
      inverted
      onClick={showReactivateModal}
      {...setData(dataPhilter, "reactivate")}
    />
  );

  const newCardButtons = (
    <div
      className={styles.buttons}
      {...setData(dataPhilter, "edit-subscription-buttons")}
    >
      {canReactivate ? reactivateButton : webEditButtons}
    </div>
  );

  const mobileServiceButtons = (
    <div
      className={styles.buttons}
      {...setData(dataPhilter, "edit-subscription-buttons")}
    >
      <Button
        buttonText={`${serviceDisplayName}`}
        data={`${dataPhilter}-mobile-service-link`}
        inverted
        shouldAddNewTab
        url={accountSubscriptionsUrl}
      />
    </div>
  );

  const promotionInfo = promoInfo && (
    <div className={styles.promotion} {...setData(dataPhilter, "promotion")}>
      <div
        className={styles.promotionLabel}
        {...setData(dataPhilter, "promotion-type")}
      >
        {promoInfo.promoType}
      </div>
      <div
        className={styles.promotionDetails}
        {...setData(dataPhilter, "promotion-details")}
      >
        {promoInfo.promoDescription}
      </div>
    </div>
  );

  const addOns = showRemoveAddOnModal &&
    associatedAddOns &&
    !!associatedAddOns.length && (
      <>
        <div className={styles.addOnsTitle}>Add ons</div>
        <div className={styles.divider} />
        <div className={styles.addOnsContainer}>
          {isSubscriptionSubmitting ? (
            <Spinner />
          ) : (
            associatedAddOns.map(addOn => {
              return (
                <div
                  className={styles.addOn}
                  key={addOn.id}
                  {...setData(dataPhilter, "add-on-card")}
                >
                  <AssociatedAddOnCard
                    addOn={addOn}
                    editMode={editMode}
                    removeAddOn={removeAddOnAction(addOn)}
                    showRemoveAddOnModal={showRemoveAddOnModal}
                  />
                </div>
              );
            })
          )}
        </div>
      </>
    );

  let upsellBanner;

  if (isUpgradeable) {
    upsellBanner = (
      <SubscriptionCardUpsells
        annualUpgrade={true}
        buttonText={upgradeDefaultText}
        site={site}
        subscriptionId={subscriptionId}
      />
    );
  } else {
    if (upsellAddOns && upsellAddOns.length > 0) {
      upsellBanner = (
        <SubscriptionCardUpsells
          addOn={upsellAddOns[0]}
          annualUpgrade={false}
          buyAddOnModalTrigger={buyAddOnModalTrigger}
          site={site}
          subscriptionId={subscriptionId}
        />
      );
    } else {
      null;
    }
  }

  const subscriptionDetails = (
    <div className={styles.subscriptionDetails}>
      <div className={styles.details}>
        {promotionInfo}
        <div className={styles.primaryDetails}>
          <div
            className={styles.subscriptionTitle}
            {...setData(dataPhilter, "subscription-name")}
          >
            {description}
          </div>
          {!isGift && (
            <div className={styles.subscriptionBillingDate}>{billingDate}</div>
          )}
        </div>
        {!isGift && (
          <div className={styles.price} {...setData(dataPhilter, "price")}>
            {realPlanAmount}
          </div>
        )}
        {statusDetails && (
          <div
            className={styles.statusDetails}
            {...setData(dataPhilter, "status")}
          >
            {statusDetails}
          </div>
        )}
      </div>
      {editMode && (isWeb ? newCardButtons : mobileServiceButtons)}
    </div>
  );

  const billingAndUpsell = nextBillingDate && plan !== PLANS.COMPED && (
    <>
      <div className={styles.divider} />
      <div className={styles.billingAndUpsell}>
        <div
          className={styles.nextBilling}
          {...setData(dataPhilter, "next-billing-date")}
        >
          <div className={styles.nextBillDate}>
            {nextBillingDateString(nextBillingDate)}
          </div>
          <div className={styles.subTotal}>
            {subTotalText(nextBillingData.subtotal)}
          </div>
          {isWeb && (
            <div className={styles.taxDetails}>
              {taxText(nextBillingData.tax)}
            </div>
          )}
          <div className={styles.totalAmount}>
            <b>{totalText(nextBillingData.total)}</b>
          </div>
        </div>
        {isWeb && canCancel && (
          <div
            className={styles.annualUpsell}
            {...setData(dataPhilter, "subscription-upsell")}
          >
            {!isSubscriptionSubmitting && upsellBanner}
          </div>
        )}
      </div>
    </>
  );

  const subscriptionCardContents = (
    <div className={styles.subscriptionCard} {...setData(dataPhilter, "")}>
      {triShadeHeader}
      <div
        className={styles.cardContentsContainer}
        {...setData(dataPhilter, "contents")}
      >
        <div className={styles.cardContentsHeader}>
          <div
            className={styles.title}
            {...setData(dataPhilter, "subscription-site")}
          >
            {site.name}
          </div>
          {!isGift && !editMode && canEdit && setEditingSubscription && (
            <Button
              buttonText={editSubscriptionText}
              className={styles.editSubscriptionButton}
              inverted
              onClick={(): void => toggleEditSubscription()}
              {...setData(dataPhilter, "edit-subscription")}
            />
          )}
        </div>
        {subscriptionDetails}
        {addOns}
        {!isGift && billingAndUpsell}
        {!!customActionButton && (
          <>
            <div className={styles.divider} />
            <div className={styles.customActionButtonWrapper}>
              {customActionButton}
            </div>
          </>
        )}
      </div>
    </div>
  );

  const subscriptionCard = (editingSubscription == null ||
    editingSubscription == subscriptionId) && (
    <>
      {editModeHeader}
      {subscriptionCardContents}
    </>
  );

  return <div>{subscriptionCard}</div>;
};

export default SubscriptionCard;
